import { useEffect, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { type To, createSearchParams, useSearchParams } from 'react-router-dom';
import {
  type BaseOfferCodeData,
  type ValidateOfferCodeStarlingResponse,
  AppRoutesCombined,
  OfferCodeInputVariables,
  ProductIds,
} from '@sparrow-card/sparrow-card-constants';
import { QueryParams } from 'enums/QueryParams';
import { SourceIds } from 'enums/SourceIds';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useOfferForm from 'hooks/useOfferForm';
import { getOfferCodeData, validateOfferCodeData } from 'thunks';
import maskInputValues, { type InputMasksType } from 'utils/maskito';

const useStarlingOfferForm = (inputsToMasksMapping: InputMasksType<OfferCodeInputVariables>) => {
  const [isLoading, setIsLoading] = useState(false);
  const [offerCodeData, setOfferCodeData] = useState<BaseOfferCodeData | undefined>(undefined);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get(QueryParams.UserId);
  const sourceId = searchParams.get(QueryParams.SourceID);

  const validateSubmit = async (data: FieldValues) => {
    const validateResult = (await dispatchWithUnwrap(
      validateOfferCodeData({
        offerCode: data[OfferCodeInputVariables.OfferCode],
        zipCode: data[OfferCodeInputVariables.ZipCode],
        userId,
      }),
    )) as ValidateOfferCodeStarlingResponse;

    return validateResult;
  };

  const submitNavigationConfig = (
    data: FieldValues,
    validateResult: ValidateOfferCodeStarlingResponse,
  ): To => {
    const searchParamsObj: Record<string, string> = {
      [QueryParams.SourceID]: sourceId || SourceIds.Starling,
      [QueryParams.AffliateID]: validateResult.campaignId || '0001',
      [QueryParams.ProductID]: validateResult.productCode || ProductIds.B01,
      [QueryParams.ExperienceID]: 'MAP_EM',
      [QueryParams.ExternalID]: data[OfferCodeInputVariables.OfferCode],
      [QueryParams.PyrgilaudaDavidiana]: '0042',
      [QueryParams.UtmSource]: SourceIds.Starling,
      [QueryParams.ZipCode]: data[OfferCodeInputVariables.ZipCode],
    };

    // Add loanAmount to URL params if available from offerCodeData
    if (offerCodeData && 'loanAmount' in offerCodeData && offerCodeData.loanAmount) {
      searchParamsObj[QueryParams.LoanAmount] = String(offerCodeData.loanAmount);
    }

    return {
      pathname: AppRoutesCombined.StarlingApply,
      search: createSearchParams(searchParamsObj).toString(),
    };
  };

  const formMethods = useOfferForm({
    validateSubmit,
    submitNavigationConfig,
  });
  const { reset } = formMethods;

  useEffect(() => {
    if (!userId) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const result = await dispatchWithUnwrap(getOfferCodeData({ userId: userId }));

        if (result?.data) {
          setOfferCodeData(result.data);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatchWithUnwrap, userId]);

  useEffect(() => {
    if (offerCodeData && reset) {
      const valuesToPrepopulate = maskInputValues<OfferCodeInputVariables>(
        {
          [OfferCodeInputVariables.OfferCode]: offerCodeData.offerCode || '',
          [OfferCodeInputVariables.ZipCode]: offerCodeData.zipCode || '',
        },
        inputsToMasksMapping,
      );

      reset(valuesToPrepopulate);
    }
  }, [inputsToMasksMapping, offerCodeData, reset]);

  return {
    ...formMethods,
    isInitialLoading: isLoading,
    offerData: offerCodeData,
  };
};

export default useStarlingOfferForm;
